import { Heart, Users, Package, ArrowRight, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { services } from '../mock';

export default function Services() {
  const serviceDetails = [
    {
      id: 1,
      icon: Heart,
      title: "Blanket Distribution",
      description: "Providing warm blankets to those facing harsh weather conditions, ensuring comfort and dignity for our community members in need.",
      details: [
        "New and gently used blankets collected from community donations",
        "Regular winter distribution events in high-need areas",
        "Emergency blanket distribution during severe weather alerts",
        "Partnership with local shelters and homeless services"
      ],
      impact: "Planned to distribute 500+ blankets in our first year"
    },
    {
      id: 2,
      icon: Users,
      title: "Food Outreach",
      description: "Distributing nutritious meals and food supplies to individuals and families experiencing food insecurity in the Dallas metro area.",
      details: [
        "Hot meals prepared and served at community events",
        "Non-perishable food packages for families in need",
        "Nutritional education and cooking classes",
        "Coordination with local food banks and pantries"
      ],
      impact: "Goal to serve 200+ meals per month to community members"
    },
    {
      id: 3,
      icon: Package,
      title: "Sanitary Supplies",
      description: "Essential hygiene products and sanitary supplies to maintain health and dignity for underserved community members.",
      details: [
        "Personal hygiene kits including soap, shampoo, toothbrushes",
        "Feminine hygiene products for women in need",
        "Baby care supplies for families with infants",
        "First aid supplies and basic health resources"
      ],
      impact: "Preparing 300+ hygiene kits for initial distribution"
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-orange-50 via-amber-50 to-orange-100 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center bg-orange-100 text-orange-800 text-sm font-medium px-4 py-2 rounded-full mb-6">
              <Heart className="h-4 w-4 mr-2" />
              Community Services
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Our Community Services
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Through Christ's love, we provide essential resources and support to bring 
              warmth, dignity, and hope to those who need it most.
            </p>
          </div>
        </div>
      </section>

      {/* Services Detail Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-20">
            {serviceDetails.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <div key={service.id} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                  <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                    <div className="bg-gradient-to-br from-orange-100 to-amber-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                      <IconComponent className="h-8 w-8 text-orange-600" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-4">{service.title}</h2>
                    <p className="text-lg text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                    
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">How We Serve:</h3>
                      <ul className="space-y-3">
                        {service.details.map((detail, detailIndex) => (
                          <li key={detailIndex} className="flex items-start">
                            <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-600">{detail}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div className="bg-orange-50 p-4 rounded-lg border border-orange-100">
                      <p className="text-orange-800 font-medium">{service.impact}</p>
                    </div>
                  </div>
                  
                  <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                    <div className="bg-gradient-to-br from-orange-100 to-amber-100 aspect-square rounded-2xl flex items-center justify-center">
                      <IconComponent className="h-24 w-24 text-orange-600" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How to Get Help Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Need Assistance?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              If you or someone you know could benefit from our services, we're here to help with dignity and compassion.
            </p>
          </div>
          
          <Card className="border-0 shadow-lg bg-white">
            <CardContent className="p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Who We Serve</h3>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-center">
                      <CheckCircle className="h-4 w-4 text-green-500 mr-3" />
                      Homeless individuals and families
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="h-4 w-4 text-green-500 mr-3" />
                      Low-income households
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="h-4 w-4 text-green-500 mr-3" />
                      Seniors in need of support
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="h-4 w-4 text-green-500 mr-3" />
                      Anyone facing temporary hardship
                    </li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">How to Access Services</h3>
                  <div className="space-y-4 text-gray-600">
                    <p>Our services are provided with no requirements other than genuine need. We serve with respect, dignity, and without judgment.</p>
                    <p>For information about upcoming distribution events or to request assistance, please contact us directly.</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-gradient-to-br from-orange-50 to-amber-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Help Us Serve More
          </h2>
          <p className="text-lg text-gray-600 mb-8 leading-relaxed">
            Your support enables us to expand our services and reach more community members in need. 
            Join us in spreading God's love through practical service.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              asChild 
              size="lg" 
              className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-3 rounded-full text-lg font-semibold transition-all duration-300 transform hover:scale-105"
            >
              <Link to="/volunteer">
                Become a Volunteer
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button 
              variant="outline" 
              size="lg" 
              asChild 
              className="border-orange-600 text-orange-600 hover:bg-orange-50 px-8 py-3 rounded-full text-lg font-semibold transition-all duration-300"
            >
              <Link to="/contact">Contact Us</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}